import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import styles from './LeaveReview.module.scss';
import CuddlesLogo from 'components/LeaveReview/UI/CuddlesLogo';
import NotFound from './UI/NotFound';
import RatingStars from './UI/RatingStars';
import FeedbackTextarea from './UI/FeedbackTextarea';
import ShareReviewLinks from './UI/ShareReviewLinks';
import PositiveTags from './UI/PositiveTags';
import SubmitButton from './UI/SubmitButton';
import { faMapPin, faPhone } from '@fortawesome/pro-light-svg-icons';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { fetchReviewInvitationInfoThunk, submitReviewThunk, ReviewsState, ReviewsStatus } from '@spike/reviews-action';
import V3Spinner from 'components/UI/V3/Spinner';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';

// Ensure submitted ratings are never 0
type Rating = 1 | 2 | 3 | 4 | 5;

interface LeaveReviewProps {
    uuid?: string;
}

export const LeaveReview: FunctionComponent<LeaveReviewProps> = ({ uuid }) => {
    const classes = styles;
    const params = useParams<{ uuid?: string }>();
    const effectiveUuid = uuid || params.uuid || '';
    useHideHubSpotChat(true);
    const dispatch = useDispatch();

    const reviewsSlice = useSelector<RootState, ReviewsState | undefined>(s => (s as any).reviews);
    const invitation = reviewsSlice?.invitation;
    const invitationLoading = reviewsSlice?.invitationLoading ?? false;
    const status = reviewsSlice?.status;
    const fetchError = status === ReviewsStatus.Error;

    const showBack = false;
    const onBack = () => {};

    // Local UI state
    const [rating, setRating] = useState<Rating | 0>(0);
    const [feedback, setFeedback] = useState<string>('');
    const [tags, setTags] = useState<string[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [finalRating, setFinalRating] = useState<Rating | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    // Track if feedback textarea was touched to show validation styling
    const [feedbackTouched, setFeedbackTouched] = useState(false);
    // Channels returned by backend after a successful 4–5 star submit
    const [shareChannels, setShareChannels] = useState<Array<{ provider: string; url: string }>>([]);
    const [businessName, setBusinessName] = useState<string>('');
    const [businessPhone, setBusinessPhone] = useState<string>('');
    //const [businessAddress, setBusinessAddress] = useState<string>('');
    const positiveTags = useMemo(
        () => ['Safety', 'Quality', 'Friendly Staff', 'Clean Facility', 'Easy Booking', 'Great Value'],
        []
    );
    const toggleTag = (t: string) => setTags(prev => (prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]));

    // Track attempt to fetch invitation
    const fetchedRef = useRef<string | null>(null);
    const [attempted, setAttempted] = useState(false);
    useEffect(() => {
        if (!effectiveUuid) return;
        if (fetchedRef.current === effectiveUuid) return;
        fetchedRef.current = effectiveUuid;
        const hostUrl = process.env.REACT_APP_HOST_URL;
        dispatch(fetchReviewInvitationInfoThunk(hostUrl as any, effectiveUuid) as any)
            .catch(() => {})
            .finally(() => setAttempted(true));
    }, [effectiveUuid, dispatch]);

    useEffect(() => {
        if (status === ReviewsStatus.InvitationFetchSuccess && invitation) {
            // Placeholder mapping: marketplaceId as business name (adjust when API expands)
            setBusinessName(invitation.marketplace.businessName || '');
            // TODO: derive address if endpoint provides it; keep blank for now
            //setBusinessAddress(invitation.marketplace.address || '');
            setBusinessPhone(invitation.marketplace.phone || '');
        }
    }, [status, invitation]);

    const businessAddress = useMemo(() => {
        const addr = invitation?.marketplace?.addresses?.[0];
        if (!addr) return '';
        return `${addr.city}, ${addr.state}, ${addr.zipcode}`;
    }, [invitation]);

    // Derived error also when no invitation after attempted fetch
    const derivedError = fetchError || (attempted && !invitationLoading && !invitation);

    const reviewLinks = useMemo(() => {
        const base = { facebook: '', google: '', yelp: '', trustpilot: '' } as Record<string, string>;
        for (const ch of shareChannels) {
            const p = (ch.provider || '').toLowerCase();
            if (p in base && ch.url) base[p] = ch.url;
        }
        return base as { facebook: string; google: string; yelp: string; trustpilot: string };
    }, [shareChannels]);

    const isLowRating = rating > 0 && rating <= 2;
    const isFeedbackRequiredAndMissing = isLowRating && !feedback.trim();

    const submitHandler = () => {
        if (rating === 0 || submitting || !invitation) return;

        // Enforce mandatory comment for 1-2 star ratings
        if (isFeedbackRequiredAndMissing) {
            setSubmitError('Comment is required for 1–2 star reviews.');
            setFeedbackTouched(true);
            return;
        }

        setSubmitting(true);
        setSubmitError(null);

        const hostUrl = process.env.REACT_APP_HOST_URL || window.location.origin;
        const payload: any = {
            token: invitation.token,
            rating,
            // Comment is required for ratings 1-2, optional for 3-5
            comment: feedback.trim() || undefined,
            tags: rating >= 4 ? tags : []
        };

        Promise.resolve(dispatch<any>(submitReviewThunk(hostUrl, payload)))
            .then((resp: any) => {
                setSubmitted(true);
                setFinalRating(rating as Rating);
                // Normalize channels array returned by service
                const incoming = resp?.channels ?? resp?.data?.channels ?? [];
                const normalized: Array<{ provider: string; url: string }> = Array.isArray(incoming)
                    ? incoming
                          .map((c: any) => ({
                              provider: String(c?.provider ?? '').toLowerCase(),
                              url: String(c?.url ?? '')
                          }))
                          .filter(c => !!c.provider && !!c.url)
                    : [];
                setShareChannels(normalized);
            })
            .catch(() => {
                setSubmitError('There was a problem submitting your review. Please try again.');
            })
            .finally(() => setSubmitting(false));
    };

    const renderSuccessMessage = (ratingValue: Rating) => {
        // Stars row
        const stars = (
            <div
                className={classes.successStars}
                role="img"
                aria-label={`${ratingValue} star rating`}
            >
                {Array.from({ length: Math.max(0, ratingValue) }, (_, i) => i + 1).map(n => (
                    <FontAwesomeIcon
                        key={n}
                        icon={faStarSolid}
                        className={classes.successStarActive}
                        aria-hidden="true"
                    />
                ))}
            </div>
        );

        let title = 'Thank you for your review.';
        let body =
            'We appreciate your feedback and regret your experience wasn’t what you expected. Your input helps us get better.';

        switch (ratingValue) {
            case 2:
                title = 'Thank you for your review!';
                body =
                    'We appreciate your feedback and regret your experience wasn’t what you expected. Your input helps us get better.';
                break;
            case 3:
                title = 'Thank you for your review.';
                body =
                    'We appreciate your honest feedback and look forward to giving you an even better experience next time.';
                break;
            case 4: {
                title = 'Thanks for 4 Stars!';
                const org = businessName || 'Business Name';
                body = `Your review has been successfully submitted! We truly appreciate your feedback and are so happy you had a great experience with ${org}.`;
                break;
            }
            case 5:
                title = 'Thanks for 5 Stars!';
                body = 'Your review helps other pet parents!';
                break;
            default:
                break;
        }

        return (
            <div className={classes.successWrapper}>
                {stars}
                <div className={classes.successTitle}>{title}</div>
                <div className={classes.successBody}>{body}</div>
            </div>
        );
    };

    if (invitationLoading) {
        return (
            <div className={classes.spinnerContainer}>
                <V3Spinner />
            </div>
        );
    }

    return (
        <div className={classes.root}>
            {!invitationLoading && (
                <div>
                    <div className={classes.titleContainer}>
                        {showBack && (
                            <div className={classes.header}>
                                <FontAwesomeIcon
                                    icon={faArrowLeft}
                                    className={classes.icon}
                                    onClick={onBack}
                                />
                                <div className={classes.titleBack}>Back</div>
                            </div>
                        )}
                        <div className={classes.title}>Leave Us a Review</div>
                    </div>
                </div>
            )}
            <div className={classes.wrapper}>
                {derivedError && (
                    <NotFound
                        customContainerClass={classes.notFoundContainer}
                        title="Oops! Booking Not Found"
                        subtitle="We couldn't find the booking you're looking for."
                    />
                )}
                {!derivedError && (
                    <div className={classes.body}>
                        {/* Store info header (always visible) */}
                        <div className={classes.storeInfo}>
                            {invitation?.marketplace?.imageUrl ? (
                                <div className={classes.logoContainer}>
                                    <img
                                        src={invitation?.marketplace?.imageUrl}
                                        alt={businessName}
                                    />
                                </div>
                            ) : (
                                <div className={classes.logoPlaceholder}>
                                    <div>Business logo</div>
                                </div>
                            )}
                            <div className={classes.storeDetails}>
                                <div className={classes.storeName}>{businessName || 'Store Name'}</div>
                                <div className={classes.storeContact}>
                                    <FontAwesomeIcon
                                        className={classes.storeInfoIcon}
                                        icon={faPhone}
                                    />{' '}
                                    {businessPhone || 'No Phone'}
                                </div>
                                <div className={classes.storeAddress}>
                                    <FontAwesomeIcon
                                        className={classes.storeInfoIcon}
                                        icon={faMapPin}
                                    />
                                    {businessAddress || 'N/A'}
                                </div>
                            </div>
                        </div>

                        {/* Body changes depending on submission state */}
                        {!submitted ? (
                            <>
                                {/* Question */}
                                <div className={classes.question}>
                                    Please rate your service experience with{' '}
                                    <span className="businessName">{businessName || 'this business'}</span>
                                </div>

                                {/* Rating */}
                                <RatingStars
                                    value={rating}
                                    onChange={(v: number) => setRating(Math.max(0, Math.min(5, v)) as Rating | 0)}
                                    className={classes.ratingRow}
                                    size={38}
                                />

                                {/* Conditional content */}
                                {rating > 0 && (
                                    <>
                                        <FeedbackTextarea
                                            rating={rating}
                                            value={feedback}
                                            onChange={val => {
                                                setFeedback(val);
                                                if (!feedbackTouched) setFeedbackTouched(true);
                                                if (submitError && val.trim()) setSubmitError(null);
                                            }}
                                            required={isLowRating}
                                            showRequiredAsterisk={isLowRating}
                                            invalid={isFeedbackRequiredAndMissing && feedbackTouched}
                                            helperText={
                                                isFeedbackRequiredAndMissing && feedbackTouched
                                                    ? 'Please help us improve by sharing what went wrong.'
                                                    : undefined
                                            }
                                        />
                                        {rating >= 4 && (
                                            <PositiveTags
                                                tags={positiveTags}
                                                selected={tags}
                                                onToggle={toggleTag}
                                                className={classes.tagsWrapper}
                                                titleClassName={classes.tagsTitle}
                                                containerClassName={classes.tagsContainer}
                                                chipClassName={classes.tagChip}
                                                chipSelectedClassName={classes.tagChipSelected}
                                            />
                                        )}
                                    </>
                                )}

                                {/* Submit */}
                                <div className={classes.submitButton}>
                                    <SubmitButton
                                        onClick={submitHandler}
                                        disabled={rating === 0 || submitting || isFeedbackRequiredAndMissing}
                                        isLoading={submitting}
                                    />
                                    {submitError && <div className={classes.errorText}>{submitError}</div>}
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Success content */}
                                {finalRating !== null && renderSuccessMessage(finalRating)}
                                {finalRating !== null && finalRating >= 4 && shareChannels.length > 0 && (
                                    <ShareReviewLinks links={reviewLinks} />
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {!derivedError && (
                <div className={classes.footer}>
                    <div className={classes.footerText}>Powered by</div>
                    <CuddlesLogo className={classes.cuddlesLogo} />
                </div>
            )}
        </div>
    );
};

export default LeaveReview;
